import React, { useState } from 'react';
import { withAnalyticsContext } from '@atlaskit/analytics-next';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	EscalationPoliciesSpotlights,
	RoutingRulesSpotlights,
	ScheduleSpotlights,
} from '@atlassian/eoc-on-call';
import { SPOTLIGHT_NAMES as TeamDashboardSpotlights } from '@atlassian/eoc-team-dashboard';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	fireUIAnalytics,
	fireScreenAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { commonMessages } from '../../../common/messages.tsx';
import { messages } from './messages.tsx';
import WelcomeModal from './welcome-modal/index.tsx';

type Props = {
	// eslint-disable-next-line jira/react/handler-naming
	stop?: () => void;
};

const OnCallTour = ({ stop }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isNewNavigation = getWillShowNav4();
	const [isModalOpen, setIsModalOpen] = useState(true);
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(null);

	const start = () => {
		setActiveSpotlight(0);

		const analyticsEvent = createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'spotlight',
		});

		fireScreenAnalytics(analyticsEvent);
	};
	const next = () => {
		const current = (activeSpotlight || 0) + 1;
		setActiveSpotlight(current);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'nextButton',
		});

		fireUIAnalytics(analyticsEvent, {
			activeSpotlight: current + 1,
		});
	};
	const back = () => {
		const current = (activeSpotlight || 1) - 1;
		setActiveSpotlight(current);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'backButton',
		});

		fireUIAnalytics(analyticsEvent, {
			activeSpotlight: current + 1,
		});
	};

	const end = () => {
		setActiveSpotlight(null);

		if (stop) {
			stop();
		}
	};

	const dismiss = () => {
		end();

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dismissButton',
		});

		const current = activeSpotlight || 0;

		fireUIAnalytics(analyticsEvent, { activeSpotlight: current + 1 });
	};

	const complete = () => {
		end();

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'okButton',
		});

		fireUIAnalytics(analyticsEvent);
	};

	const learnMore = () => {
		end();

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'learnMoreButton',
		});

		fireUIAnalytics(analyticsEvent);
	};

	const renderActiveSpotlight = () => {
		const spotlights = [
			<JiraSpotlight
				actionsBeforeElement="1/4"
				actions={[
					{ onClick: next, text: formatMessage(commonMessages.next) },
					{
						onClick: dismiss,
						text: formatMessage(commonMessages.dismiss),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.sidebarTitle)}
				target={TeamDashboardSpotlights.sidebarItems}
				key={TeamDashboardSpotlights.sidebarItems}
				dialogPlacement={isNewNavigation ? 'bottom left' : 'right top'}
				targetRadius={3}
				targetBgColor={token('elevation.surface', N0)}
				messageId="eoc-on-call.ui.tours.get-started.jira-spotlight"
				messageType="transactional"
			>
				{formatMessage(messages.sidebarDescription)}
			</JiraSpotlight>,
			<JiraSpotlight
				actionsBeforeElement="2/4"
				actions={[
					{ onClick: next, text: formatMessage(commonMessages.next) },
					{
						onClick: back,
						text: formatMessage(commonMessages.back),
						appearance: 'subtle',
					},
					{
						onClick: dismiss,
						text: formatMessage(commonMessages.dismiss),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.routingRuleTitle)}
				target={RoutingRulesSpotlights.lastEntity}
				key={RoutingRulesSpotlights.lastEntity}
				targetRadius={3}
				messageId="eoc-on-call.ui.tours.get-started.jira-spotlight.1"
				messageType="transactional"
			>
				{formatMessage(messages.routingRuleDescription)}
			</JiraSpotlight>,
			<JiraSpotlight
				actionsBeforeElement="3/4"
				actions={[
					{ onClick: next, text: formatMessage(commonMessages.next) },
					{
						onClick: back,
						text: formatMessage(commonMessages.back),
						appearance: 'subtle',
					},
					{
						onClick: dismiss,
						text: formatMessage(commonMessages.dismiss),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.escalationPoliciesTitle)}
				target={EscalationPoliciesSpotlights.firstEntity}
				key={EscalationPoliciesSpotlights.firstEntity}
				targetRadius={3}
				messageId="eoc-on-call.ui.tours.get-started.jira-spotlight.2"
				messageType="transactional"
			>
				{formatMessage(messages.escalationPoliciesDescription)}
			</JiraSpotlight>,
			<JiraSpotlight
				actionsBeforeElement="4/4"
				actions={[
					{ onClick: complete, text: formatMessage(commonMessages.ok) },
					{
						onClick: back,
						text: formatMessage(commonMessages.back),
						appearance: 'subtle',
					},
					{
						onClick: learnMore,
						text: formatMessage(commonMessages.learnMore),
						appearance: 'subtle',
						href: 'https://operations-help.atlassian.net/wiki/spaces/OPSHELP/pages/8290305/How+do+on-call+schedules+work+in+operations',
						target: '_blank',
					},
				]}
				heading={formatMessage(messages.scheduleTitle)}
				target={ScheduleSpotlights.firstSchedule}
				key={ScheduleSpotlights.firstSchedule}
				targetRadius={3}
				messageId="eoc-on-call.ui.tours.get-started.jira-spotlight.3"
				messageType="transactional"
			>
				{formatMessage(messages.scheduleDescription)}
			</JiraSpotlight>,
		];

		if (activeSpotlight === null) {
			return null;
		}

		return spotlights[activeSpotlight];
	};

	return (
		<SpotlightTransition>
			<WelcomeModal
				isActive={isModalOpen}
				onStart={() => {
					start();
					setIsModalOpen(false);
				}}
				onCancel={() => {
					end();
					setIsModalOpen(false);
				}}
			/>
			{renderActiveSpotlight()}
		</SpotlightTransition>
	);
};

export default withAnalyticsContext({
	source: 'OpsDashboardSpotlightModal',
	tags: ['spotlight', 'ops-dashboard'],
})(OnCallTour);
